<script setup lang="ts">
import { computed, ref } from "vue";
import { UiButton } from "@shopexperts/shared";
import ProjectDetailsStep, {
  type ProjectDetailsData,
} from "./create-project/ProjectDetailsStep.vue";
import ContactDetailsStep, {
  type ContactDetailsData,
} from "./create-project/ContactDetailsStep.vue";
import SuccessStep from "./leads/SuccessStep.vue";
import { useSubmitExpertLeadInvite } from "@/app/composables/leads/useSubmitExpertLeadInvite";

const props = defineProps<{ directThreadId?: string; peerName?: string }>();

const emit = defineEmits<{
  (e: "submitted"): void;
  (e: "close"): void;
}>();

const totalSteps = 2;
const currentStep = ref<1 | 2 | "success">(1);

const projectDetailsData = ref<ProjectDetailsData>({
  email: "",
  projectTitle: "",
  projectDescription: "",
  urgency: undefined,
  projectValueRangeId: undefined,
});

const clientDetailsData = ref<ContactDetailsData>({
  storeUrl: "",
  noStoreYet: false,
  brandName: "",
  firstName: "",
  lastName: "",
});

const { submitLeadInvite, isSubmitting } = useSubmitExpertLeadInvite();

const browserTimezone = computed(() => {
  if (typeof window === "undefined") return undefined;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone || undefined;
});

function handleProjectDetailsNext(data: ProjectDetailsData) {
  projectDetailsData.value = data;
  currentStep.value = 2;
}

function handleClientDetailsBack() {
  currentStep.value = 1;
}

async function handleClientDetailsSubmit(data: ContactDetailsData) {
  clientDetailsData.value = data;

  try {
    await submitLeadInvite({
      directThreadId: props.directThreadId,
      email: projectDetailsData.value.email,
      firstName: data.firstName,
      lastName: data.lastName,
      brandName: data.brandName,
      storeUrl: data.storeUrl,
      projectTitle: projectDetailsData.value.projectTitle,
      projectBrief: projectDetailsData.value.projectDescription || undefined,
      urgency:
        projectDetailsData.value.urgency || "open_to_expert_opinion",
      timezone: browserTimezone.value,
      projectValueRangeId: projectDetailsData.value.projectValueRangeId,
    });
  } catch {
    return;
  }

  currentStep.value = "success";
  emit("submitted");
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <ProjectDetailsStep
      v-if="currentStep === 1"
      v-model="projectDetailsData"
      :current-step="1"
      :total-steps="totalSteps"
      caption="Client Details"
      :heading="peerName ? `Start a project with ${peerName}` : 'Who should we invite?'"
      :subheading="peerName ? 'Confirm this client’s contact details and describe the project you’re starting together.' : 'Start with the client’s email and the project details so we can create the project for them.'"
      email-label="Client Email Address"
      email-placeholder="client@example.com"
      :show-login-hint="false"
      :show-attachment-note="false"
      @next="handleProjectDetailsNext"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </ProjectDetailsStep>

    <ContactDetailsStep
      v-else-if="currentStep === 2"
      v-model="clientDetailsData"
      :current-step="2"
      :total-steps="totalSteps"
      caption="Store Details"
      heading="Add the client and store details"
      subheading="Finish the invite with the client's name, brand, and Shopify store URL."
      store-label="Client Shopify Store URL"
      store-placeholder="client-store.myshopify.com"
      brand-label="Brand Name (optional)"
      brand-placeholder="Taylor Co"
      submit-label="Create Project"
      :is-loading="isSubmitting"
      :allow-no-store-yet="false"
      @back="handleClientDetailsBack"
      @next="handleClientDetailsSubmit"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </ContactDetailsStep>

    <SuccessStep
      v-else
      :has-selected-expert="true"
      :disable-auto-redirect="true"
      title="Project created and client notified"
      description="The project has been created successfully. Your client has been notified by email and can now access the project."
    >
      <template #actions>
        <UiButton
          variant="primary"
          label="Close"
          class="min-w-[200px]"
          @click="emit('close')"
        />
      </template>
    </SuccessStep>
  </div>
</template>
